import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OfferPageState, coverByOffersStates, takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { arrayToObject, EmptyArray, min, trimArray } from 'lib/array/arrayUtils'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import { createSelector } from 'reselect'
import moment from 'moment'
import {
  BaseOfferPackageOptionView,
  OfferPackageOptionView, getCalendarMonths,
  getDurationPackageOptions,
  CalendarPickerMonthView,
  getBundledPackages,
} from './offerPageSelectors'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { EmptyObject } from 'lib/object/objectUtils'
import { isBundleOffer } from 'lib/offer/offerTypes'
import { getSelectedOfferPageMultiRoomDay } from './multipleRoomBookingSelectors'

export interface BundleOfferPackageOptionView extends BaseOfferPackageOptionView {
  bundlePackageId: string
  bundlePackageName: string
  bundlePackageDescription: string
  options: Array<OfferPackageOptionView>;
}

export const getComplementaryBundleState = createSelector(
  (state: OfferPageState) => state.paginator,
  (state: OfferPageState) => state.bundledOffers,
  (paginator, bundledOffers) => {
    return bundledOffers && paginator ? Object.values(bundledOffers).find(o => o.offer?.id !== paginator.currentOfferId) : EmptyObject
  },
)

export const getBundleDurationPackageOptions = createSelector(
  (state: OfferPageState, appState: App.State) => coverByOffersStates<OfferPackageOptionView>(getDurationPackageOptions, state, appState).flat(),
  getBundledPackages,
  (packageOptions, bundlePackages): { [bundlePackageId: string]: Array<OfferPackageOptionView> } | undefined => {
    if (bundlePackages) {
      return Object.keys(bundlePackages).reduce<Record<string, Array<OfferPackageOptionView>>>((acc, bundlePackageId) => {
        const packagesData = bundlePackages[bundlePackageId]
        const allAllowPackages = packagesData.map(pkg => pkg.uniqueKey)

        acc[bundlePackageId] = packageOptions.filter(view => allAllowPackages.includes(view.package.uniqueKey))
        return acc
      }, {})
    }

    return undefined
  },
)

export const getAvailableBundlePackages = createSelector(
  (state: OfferPageState, appState: App.State) => getBundleDurationPackageOptions(state, appState),
  getBundledPackages,
  (state: OfferPageState) => state.offer as App.BundleOffer,
  (state: OfferPageState) => state.bundledOffers,
  (packageOptions = {}, bundlePackages, offer, stateBundledOffers = {}): { [bundlePackageId: string]: BundleOfferPackageOptionView } | undefined => {
    if (!bundlePackages) {
      return undefined
    }

    return Object.keys(bundlePackages).reduce<Record<string, BundleOfferPackageOptionView>>((acc, bundlePackageId) => {
      const packagesData = bundlePackages[bundlePackageId]
      const allAllowPackages = packagesData.map(pkg => pkg.uniqueKey)
      const options = packageOptions[bundlePackageId].filter((o) => allAllowPackages.includes(o.package.uniqueKey))
      const bundledOffersIds = Object.keys(offer.bundledOffers ?? {})

      const hotelAvailable = options.length === bundledOffersIds.length ? options.every((o) => {
        const bundledOffer = offer.bundledOffers[o.package.offerId]
        const useDynamicCancellationPolicies = bundledOffer.property.useDynamicCancellationPolicies ?? false

        const isAvailable = o.hotelPrice && o.hotelAvailable

        if (useDynamicCancellationPolicies && stateBundledOffers[o.package.offerId]?.checkInDate) {
          return isAvailable && o.hasAvailableRates
        }

        return isAvailable
      }) : false

      acc[bundlePackageId] = {
        bundlePackageId,
        options,
        bundlePackageName: offer.bundlePackages[bundlePackageId].name,
        bundlePackageDescription: offer.bundlePackages[bundlePackageId].description ?? '',
        basePrice: 0,
        baseValue: 0,
        surcharge: 0,
        extraGuestsSurcharge: 0,
        hotelPrice: 0,
        hotelTotal: 0,
        hotelValue: 0,
        flightPrice: 0,
        taxesAndFees: 0,
        propertyFees: 0,
        hotelDiscountPercent: 0,
        bundledDiscountPercent: 0,
        availableRooms: min(options, option => option.availableRooms ?? 0)?.availableRooms ?? 0,
        hotelAvailable,
        capacityValid: options.length === bundledOffersIds.length ? options.every((o) => o.capacityValid) : false,
        flightsAvailable: false,
        fromCalendarInfo: false,
        hasAvailableRates: undefined,
        refundable: false,
        pricing: {
          price: 0,
          value: 0,
          discountPercent: 0,
          fees: [],
          saleUnit: offer?.saleUnit ?? '',
        },
      }

      for (const option of options) {
        const hotelPrice = acc[bundlePackageId].hotelPrice + option.hotelPrice
        const hotelValue = acc[bundlePackageId].hotelValue + option.hotelValue
        const hotelTotal = acc[bundlePackageId].hotelTotal + option.hotelTotal

        const hotelDiscountPercent = getValueOffPercent(hotelValue, hotelPrice)
        acc[bundlePackageId] = {
          ...acc[bundlePackageId],
          basePrice: acc[bundlePackageId].basePrice + option.basePrice,
          baseValue: acc[bundlePackageId].baseValue + option.baseValue,
          surcharge: acc[bundlePackageId].surcharge + option.surcharge,
          extraGuestsSurcharge: acc[bundlePackageId].extraGuestsSurcharge + option.extraGuestsSurcharge,
          hotelPrice,
          hotelValue,
          hotelTotal,
          taxesAndFees: acc[bundlePackageId].taxesAndFees + option.taxesAndFees,
          propertyFees: acc[bundlePackageId].propertyFees + option.propertyFees,
          hotelDiscountPercent,
          bundledDiscountPercent: hotelDiscountPercent,
        }
      }

      acc[bundlePackageId].pricing = {
        price: acc[bundlePackageId].hotelTotal,
        value: acc[bundlePackageId].hotelValue,
        discountPercent: getValueOffPercent(acc[bundlePackageId].hotelValue, acc[bundlePackageId].hotelTotal),
        fees: [
          { type: 'taxesAndFees', amount: acc[bundlePackageId].taxesAndFees },
          { type: 'property', amount: acc[bundlePackageId].propertyFees },
        ],
        saleUnit: offer?.saleUnit ?? '',
      }

      return acc
    }, {})
  },
)

export const getBundleCalendarMonths = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state, appState): { months: Array<CalendarPickerMonthView>, availableCount: number } => {
    const allCalendarMonths = coverByOffersStates<{ months: Array<CalendarPickerMonthView>, availableCount: number }>(getCalendarMonths, appState, state).map(x => x.months)

    const bundleOfferCalendarMonths = allCalendarMonths.reduce((resultMonths, months, index) => {
      if (index === 0) {
        return arrayToObject(months, month => month.key)
      } else {
        const lastOfferMonths = Object.values(resultMonths)
        const nextOfferMonths = arrayToObject(months, month => month.key)
        for (const lastMonth of lastOfferMonths) {
          const month = nextOfferMonths[lastMonth.key]
          if (month) {
            resultMonths[month.key] = {
              ...resultMonths[month.key],
              hotelAvailable: resultMonths[month.key].hotelAvailable && month.hotelAvailable,
              packagedAvailable: resultMonths[month.key].packagedAvailable && month.packagedAvailable,
              hotelTotal: resultMonths[month.key].hotelTotal + month.hotelTotal,
              packagedTotal: resultMonths[month.key].packagedTotal + month.packagedTotal,
              limitedAvailability: resultMonths[month.key].limitedAvailability && month.limitedAvailability,
              flightCacheActive: resultMonths[month.key].flightCacheActive && month.flightCacheActive,
              hasFlightDeal: resultMonths[month.key].hasFlightDeal && month.hasFlightDeal,
            }
          } else {
            resultMonths[lastMonth.key] = {
              ...resultMonths[lastMonth.key],
              hotelAvailable: false,
              packagedAvailable: false,
            }
          }
        }
      }
      return resultMonths
    }, {} as { [key: string]: CalendarPickerMonthView })

    return {
      months: Object.values(bundleOfferCalendarMonths),
      availableCount: trimArray(Object.values(bundleOfferCalendarMonths), month => !month.hotelAvailable).length,
    }
  },
)

export const getBundledOffersAvailableRates = createSelector(
  (state: App.State) => state.offer.offerAvailableRates,
  (state: App.State, offerState: OfferPageState) => offerState.bundledOffers,
  (state: App.State, offerState: OfferPageState) => offerState.rooms,
  (offerAvailableRates, bundledOffers, rooms) => {
    if (offerAvailableRates && bundledOffers) {
      return Object.values(bundledOffers).map((o) => {
        const availableRatesKey = o.checkInDate && rooms ? buildAvailableRateKey(o.checkInDate, moment(o.checkInDate).add(o.duration, 'days').format(ISO_DATE_FORMAT), rooms) : ''
        return offerAvailableRates?.[o.offer?.id ?? '']?.[availableRatesKey]
      })
    }

    return null
  },
)

export const areBundlePricesLoading = createSelector(
  (state: App.State) => state.calendar.calendarsLoading,
  getBundledOffersAvailableRates,
  (state: App.State, offerState: OfferPageState) => getBundledPackages(offerState),
  (calendars, bundledOfferRates, bundlePackages) => {
    const packagesData = Object.values(bundlePackages ?? {}).flat()
    const allAllowPackages = packagesData.map(pkg => pkg.uniqueKey)

    return allAllowPackages.some((uniqueKey) => calendars[uniqueKey]) || bundledOfferRates?.some((o) => o?.fetching)
  },
)

export interface BundleSelectedDaysInfo {
  duration: number,
  hotelTotal: number,
  available: boolean,
  taxesAndFees: number,
}

const emptySelectedDays: BundleSelectedDaysInfo = {
  duration: 0,
  hotelTotal: 0,
  available: false,
  taxesAndFees: 0,
}

export const getBundleSelectedDays = createSelector(
  (state: App.State, offerState: OfferPageState) => !isBundleOffer(offerState.offer),
  (state: App.State, offerState: OfferPageState) => getAvailableBundlePackages(offerState, state),
  (state: App.State, offerState: OfferPageState) => coverByOffersStates<App.CalendarDay>(getSelectedOfferPageMultiRoomDay, offerState, state),
  (state: App.State, offerState: OfferPageState) => offerState.bundledOffers,
  (state: App.State, offerState: OfferPageState) => takeCurrentOfferState(offerState).filteredBundlePackageId,
  (disabled, availableBundlePackages, selectedDays = EmptyArray, stateBundledOffers, filteredBundlePackageId) => {
    if (disabled || !stateBundledOffers) {
      return emptySelectedDays
    }

    const bundledOffers = Object.values(stateBundledOffers)
    const isBundleOffer = bundledOffers.length > 0
    if (!isBundleOffer) {
      return emptySelectedDays
    }

    let availableBundlePackage: BundleOfferPackageOptionView | undefined
    if (availableBundlePackages) {
      availableBundlePackage = filteredBundlePackageId ? availableBundlePackages?.[filteredBundlePackageId] : min(Object.values(availableBundlePackages), p => p.hotelPrice || Number.MAX_SAFE_INTEGER)
    }

    return selectedDays.reduce((acc, item, index) => {
      const currentOffer = bundledOffers[index]
      const currentOfferOption = availableBundlePackage?.options?.find(o => o.package.offerId === currentOffer.offer.id)
      const hotelTotal = !item?.hotelTotal && currentOfferOption ? (currentOfferOption.hotelPrice + currentOfferOption.propertyFees) : (item?.hotelTotal ?? 0)

      return {
        duration: acc.duration + currentOffer.duration,
        hotelTotal: acc.hotelTotal + hotelTotal,
        available: true,
        taxesAndFees: acc.taxesAndFees + (currentOfferOption?.propertyFees ?? 0),
      }
    }, {
      duration: 0,
      hotelTotal: 0,
      available: false,
      taxesAndFees: 0,
    })
  },

)
